import { Injectable, inject } from '@angular/core'
import { Observable, catchError, of, tap, finalize, map } from 'rxjs'
import { BaseAsyncService } from '@core/state/base-service'
import { DocumentsStore } from '@core/state/documents.store'
import {
  DocumentTypeOptionDto,
  DocumentTypeOptionsApiResponse,
  SuccessionFileDocumentsDtoApiOneSuccess,
  UploadDocumentApiResponse,
  UploadedDocumentDto,
} from '@shared/models/dto/api-dtos'

/** Paramètres d'un upload de document vers la GED EGG2. */
export interface DocumentUploadInput {
  folderId: string
  /** Code d'emplacement GED (ex. CLESAME_PIECES_HERITIERS). */
  locationCode: string
  /** Identifiant EGG du type de document (sélecteur de la modale). */
  type: string
  label: string
  heirIds?: string[]
  file: File
}

@Injectable({ providedIn: 'root' })
export class DocumentsService extends BaseAsyncService {
  private readonly store = inject(DocumentsStore)

  loadDocuments(
    successionFileId: string,
    searchTerm?: string,
    phase?: string
  ): Observable<SuccessionFileDocumentsDtoApiOneSuccess['data'] | null> {
    this.store.setLoading(true)
    this.store.setError(null)

    const endpoint = this.buildEndpoint(successionFileId, searchTerm, phase)

    return this.get<SuccessionFileDocumentsDtoApiOneSuccess>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(0, 800, 1.5),
    }).pipe(
      map(response => response?.data ?? null),
      tap(data => {
        if (data) {
          this.store.setDocuments(data)
        }
      }),
      catchError(error => {
        console.error('Error loading documents', error)
        this.store.setError('Impossible de charger les documents')
        return of(null)
      }),
      finalize(() => this.store.setLoading(false))
    )
  }

  downloadDocument(documentId: string, filename: string): Observable<void> {
    return this.getBlob(`/documents/${documentId}/download`).pipe(
      tap(blob => {
        const resolvedFilename = this.ensureExtension(filename, blob.type)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = resolvedFilename
        a.click()
        URL.revokeObjectURL(url)
      }),
      map(() => void 0)
    )
  }

  private ensureExtension(filename: string, mimeType: string): string {
    const lastDot = filename.lastIndexOf('.')
    const suffix = lastDot === -1 ? '' : filename.slice(lastDot + 1)
    const hasExtension = /^[a-zA-Z][a-zA-Z0-9]{0,4}$/.test(suffix)
    if (hasExtension) return filename
    const ext = this.mimeToExtension(mimeType)
    return ext ? `${filename}.${ext}` : filename
  }

  private mimeToExtension(mimeType: string): string | null {
    const map: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-excel': 'xls',
      'text/plain': 'txt',
      'application/zip': 'zip',
    }
    return map[mimeType] ?? null
  }

  getPreviewBlob(documentId: string): Observable<Blob> {
    return this.getBlob(`/documents/${documentId}/download?preview=true`)
  }

  /** Récupère les types de documents EGG (pour le sélecteur de la modale d'upload). */
  getDocumentTypes(): Observable<DocumentTypeOptionDto[]> {
    return this.get<DocumentTypeOptionsApiResponse>('/documents/document-types').pipe(map(res => res?.data ?? []))
  }

  /** Téléverse un document dans la GED EGG2 (multipart/form-data). */
  uploadDocument(input: DocumentUploadInput): Observable<UploadedDocumentDto | null> {
    const form = new FormData()
    form.append('folderId', input.folderId)
    form.append('locationCode', input.locationCode)
    form.append('type', input.type)
    form.append('label', input.label)
    ;(input.heirIds ?? []).forEach(id => form.append('heirIds', id))
    form.append('file', input.file, input.file.name)

    return this.post<UploadDocumentApiResponse>('/documents/upload', form).pipe(map(res => res?.data ?? null))
  }

  private buildEndpoint(successionFileId: string, searchTerm?: string, phase?: string): string {
    const params = new URLSearchParams()
    if (searchTerm) params.set('searchTerm', searchTerm)
    if (phase) params.set('phase', phase)
    const query = params.toString()
    return `/documents/succession-file/${successionFileId}${query ? `?${query}` : ''}`
  }
}
